/**
 * RoleFormDialog Component
 *
 * Dialog for creating and editing roles with permission selection.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogContent,
  TextField,
  Stack,
  Typography,
  Tabs,
  Tab,
  Box,
  Alert,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import type { Role } from '@/hooks';
import { PermissionMatrix } from './PermissionMatrix';

interface RoleFormDialogProps {
  open: boolean;
  role?: Role | null;
  onClose: () => void;
  onSave: (roleData: RoleFormData) => void;
  saving?: boolean;
}

export interface RoleFormData {
  name: string;
  description: string;
  permissionIds: string[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export function RoleFormDialog({ open, role, onClose, onSave, saving }: RoleFormDialogProps) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const isEdit = Boolean(role);
  const [tabValue, setTabValue] = useState(0);

  const [formData, setFormData] = useState<RoleFormData>({
    name: '',
    description: '',
    permissionIds: [],
  });

  const [errors, setErrors] = useState<Partial<Record<keyof RoleFormData, string>>>(
    {}
  );
  const [isDirty, setIsDirty] = useState(false);

  // Initialize form when role changes
  useEffect(() => {
    if (role) {
      setFormData({
        name: role.name,
        description: role.description ?? '',
        permissionIds: role.permissionIds,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        permissionIds: [],
      });
    }
    setErrors({});
    setTabValue(0);
    setIsDirty(false);
  }, [role, open]);

  const validateForm = (): Partial<Record<keyof RoleFormData, string>> => {
    const newErrors: Partial<Record<keyof RoleFormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('roleForm.nameRequired');
    } else if (formData.name.length < 3) {
      newErrors.name = t('roleForm.nameMinLength');
    }

    if (formData.permissionIds.length === 0) {
      newErrors.permissionIds = t('roleForm.permissionRequired');
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleSave = () => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      // Show the tab that holds the first error; otherwise a basic-info error
      // raised from the permissions tab looks like the save did nothing (#2641).
      const basicInfoError = Boolean(newErrors.name || newErrors.description);
      const errorTab = basicInfoError ? 0 : 1;
      if (tabValue !== errorTab) {
        setTabValue(errorTab);
      }
      return;
    }
    onSave(formData);
  };

  const handleClose = () => {
    setFormData({ name: '', description: '', permissionIds: [] });
    setErrors({});
    setTabValue(0);
    setIsDirty(false);
    onClose();
  };

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      title={isEdit ? t('roleForm.editTitle') : t('roleForm.createTitle')}
      isDirty={isDirty}
      onSubmit={handleSave}
      isLoading={saving}
      submitLabel={isEdit ? tc('save') : t('roleForm.createRole')}
      maxWidth="md"
    >
      <DialogContent dividers>
        <Tabs value={tabValue} onChange={(_, v) => { setTabValue(v); }}>
          <Tab label={t('roleForm.basicInfoTab')} />
          <Tab label={t('roleForm.permissionsTab')} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Stack spacing={3}>
            {role?.isSystem && (
              <Alert severity="warning">
                {t('roleForm.systemRoleWarning')}
              </Alert>
            )}

            <TextField
              label={t('roleForm.roleName')}
              fullWidth
              required
              autoFocus
              value={formData.name}
              onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setIsDirty(true); }}
              error={Boolean(errors.name)}
              helperText={errors.name || t('roleForm.roleNameHint')}
              disabled={role?.isSystem}
            />

            <TextField
              label={t('roleForm.description')}
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => {
                setFormData({ ...formData, description: e.target.value });
                setIsDirty(true);
              }}
              error={Boolean(errors.description)}
              helperText={errors.description || t('roleForm.descriptionHint')}
            />

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>{t('roleForm.currentPermissions')}:</strong>{' '}
                {t('roleForm.selectedCount', { count: formData.permissionIds.length })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('roleForm.permissionsTabHint')}
              </Typography>
            </Box>
          </Stack>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Stack spacing={2}>
            {errors.permissionIds && (
              <Alert severity="error">{errors.permissionIds}</Alert>
            )}

            <Typography variant="body2" color="text.secondary">
              {t('roleForm.permissionsDescription')}
            </Typography>

            <PermissionMatrix
              selectedPermissionIds={formData.permissionIds}
              onChange={(permissionIds) => {
                setFormData({ ...formData, permissionIds });
                setIsDirty(true);
              }}
            />
          </Stack>
        </TabPanel>
      </DialogContent>
    </FormDialog>
  );
}
